import React, { useContext } from 'react';
import { Nullable } from 'common/types/nullable';
import RowCountContext from './RowCountContext';
import { RowCountType } from './types';

export default class RowCountUtils {
  /** Checks URL parameters for disableRowCountCache flag. */
  static getCachingSettingFromURL(): boolean {
    if (typeof window === 'undefined') return true; // Default for SSR

    const params = new URLSearchParams(window.location.search);
    const disableCache = params.get('disableRowCountCache');

    // Return false if param explicitly set to 'true', otherwise default to caching enabled
    return disableCache !== 'true';
  }

  /** Checks URL parameters for debug enable flag. */
  static getDebugSettingFromURL(name: string = 'enableRowCountDebug'): boolean {
    if (typeof window === 'undefined') return false; // Default for SSR

    const params = new URLSearchParams(window.location.search);
    const enableDebug = params.get(name);

    // Return true if param explicitly set to 'true', otherwise default to debug disabled
    return enableDebug === 'true';
  }
}

/** Hook to get row count with loading and error states - only loads the requested type. */
export const useRowCount = (
  type: RowCountType = RowCountType.Total,
): {
  value: Nullable<number>;
  loading: boolean;
  error: Nullable<string>;
} => {
  const context = useContext(RowCountContext);

  const contextValue = type === RowCountType.Total ? context?.totalCount : context?.filteredCount;
  const result =
    context && contextValue
      ? {
          value: contextValue.value,
          loading: contextValue.loading,
          error: contextValue.error,
        }
      : { value: null, loading: false, error: 'useRowCount must be used within a RowCountProvider' };

  // Track usage when a component accesses a count value (for debug purposes)
  React.useEffect(() => {
    if (context?.debugTracker && result.value !== null) {
      context.debugTracker.trackUsage(result.value);
    }
  }, [context?.debugTracker, result.value, result.loading, type]);

  return result;
};

/** Safe hook that returns null values when context errors occur instead of throwing. */
export const useSafeRowCount = (countType: RowCountType) => {
  const result = useRowCount(countType);
  return result.error ? { value: null, loading: false, error: null } : result;
};
